import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DomainTuple,
  Point,
  VictoryArea,
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryLine,
  VictoryScatter,
  VictoryTooltip,
} from 'victory';
import ChartTooltip, {
  TOOLTIP_WIDTH,
  TooltipModel,
  tooltipModelHeight,
} from './CustomTooltip';
import CrosshairTooltip from '../../../../../../components/Charts/CrosshairTooltip';
import { colors } from '../../../../../../colors';
import {
  formatBalance,
  formatPercentage,
  formatPrice,
} from '../../../../../../helpers/NumberFormatter';
import { getBotColor } from '../../../../../Overview/ClusterItem/Charts/helpers';
import {
  ChartType,
  SingleProfitCalculationApiResponse,
} from '../../../../types';
import ChartTypeSelector from './ChartTypeSelector';
import { Transaction, CompactTransaction } from '../../../../types';

import './index.css';
import ChartZoomOptions from './ChartZoomOptions';
import ChartZoomLevel from './ChartZoomLevel';
import {
  calculateIsColorTooDark,
  lightenColor,
} from '../../../../../../helpers/colors';
import {
  createXTickValues,
  getFormatUnit,
} from '../../../../../../helpers/bundleTransactionsToCompactForm';
import { ComponentDimensions } from '../../../../../../types/Layout';
import {
  buildTimeAxis,
  calculateLongestTickWidth,
  calculateYAxisWidth,
  niceLinearTicks,
  stretchYValueOnSharedDomain,
} from '../../../../../../helpers/Chart';
import useAnimatedChartData from '../../../../../../hooks/useAnimatedChartData';
import TransactionTypeToggles from './TransactionTypeToggles';

interface TransactionTypeToggleValues {
  core: boolean;
  takeProfit: boolean;
  stopLoss: boolean;
}

interface Props {
  data: SingleProfitCalculationApiResponse;
  allCompactTransactions: CompactTransaction[];
  rootDimensions: ComponentDimensions;
  setIsChartAnimationDone: React.Dispatch<React.SetStateAction<boolean>>;
  // Optional controlled state overrides.
  selectedChartTypesOverride?: ChartType[];
  onSelectedChartTypesChange?: (
    next: ChartType[] | ((prev: ChartType[]) => ChartType[]),
  ) => void;
  isTransactionsSelectedOverride?: boolean;
  onIsTransactionsSelectedChange?: (
    next: boolean | ((prev: boolean) => boolean),
  ) => void;
  transactionTypeToggleValuesOverride?: TransactionTypeToggleValues;
  onTransactionTypeToggleValuesChange?: (
    next:
      | TransactionTypeToggleValues
      | ((prev: TransactionTypeToggleValues) => TransactionTypeToggleValues),
  ) => void;
  hideControls?: boolean;
  hideXAxis?: boolean;
  hideGrid?: boolean;
  /** Draw a faint vertical gridline at each adaptive time-axis boundary. */
  showTimeGridlines?: boolean;
  disableAnimation?: boolean;
  onLeftAxisWidthChange?: (width: number) => void;
  /** Reports the plot rectangle + time domain so an external x-axis footer
   * can align its cells under the chart's vertical gridlines. */
  onTimeAxisChange?: (info: {
    paddingLeft: number;
    paddingRight: number;
    domainStart: number;
    domainEnd: number;
  }) => void;
}

type Datum = {
  x: number;
  y: number;
  color: string;
  isBuy: boolean;
};

const shouldAnimate = false;
const animate = shouldAnimate
  ? {
      onEnter: { duration: 0 },
      onLoad: { duration: 0 },
    }
  : undefined;

const xDomainBuffer = 0.1;

/**
 * 
 * {
    x: [1672750799999, 1675918799999],
    y: [0.30286238237011587, 1],
  }
 */

const SingleProfitCalculationResultChart = (props: Props) => {
  const {
    data,
    allCompactTransactions,
    rootDimensions,
    setIsChartAnimationDone,
    selectedChartTypesOverride,
    onSelectedChartTypesChange,
    isTransactionsSelectedOverride,
    onIsTransactionsSelectedChange,
    transactionTypeToggleValuesOverride,
    onTransactionTypeToggleValuesChange,
    hideControls,
    hideXAxis,
    hideGrid,
    showTimeGridlines,
    disableAnimation,
    onLeftAxisWidthChange,
    onTimeAxisChange,
  } = props;

  const [internalSelectedChartTypes, setInternalSelectedChartTypes] = useState([
    ChartType.StableBalance,
    ChartType.NoAlgoritmicProgression,
  ]);
  const selectedChartTypes =
    selectedChartTypesOverride ?? internalSelectedChartTypes;
  const setSelectedChartTypes: React.Dispatch<
    React.SetStateAction<ChartType[]>
  > = onSelectedChartTypesChange ?? setInternalSelectedChartTypes;

  const [zoomedDomain, setZoomedDomain] = useState<{
    x: DomainTuple;
    y: DomainTuple;
  }>();
  const [zoomedStartIndex, setZoomedStartIndex] = useState<number>();
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [areTransactionProfitsVisible, setAreTransactionProfitsVisible] =
    useState(false);

  const [internalIsTransactionsSelected, setInternalIsTransactionsSelected] =
    useState(true);
  const isTransactionsSelected =
    isTransactionsSelectedOverride ?? internalIsTransactionsSelected;
  const setIsTransactionsSelected: React.Dispatch<
    React.SetStateAction<boolean>
  > = onIsTransactionsSelectedChange ?? setInternalIsTransactionsSelected;

  const useTakeProfitTransactions = data.useTakeProfitTransactions;
  const [
    internalTransactionTypeToggleValues,
    setInternalTransactionTypeToggleValues,
  ] = useState({
    core: true,
    takeProfit: false,
    stopLoss: false,
  });
  const transactionTypeToggleValues =
    transactionTypeToggleValuesOverride ?? internalTransactionTypeToggleValues;
  const setTransactionTypeToggleValues: React.Dispatch<
    React.SetStateAction<TransactionTypeToggleValues>
  > =
    onTransactionTypeToggleValuesChange ??
    setInternalTransactionTypeToggleValues;

  const selectedChartTypesAxis = useMemo(() => {
    const axis = [];

    if (selectedChartTypes.includes(ChartType.StableBalance)) {
      axis.push(ChartType.StableBalance);
    }

    if (selectedChartTypes.includes(ChartType.AssetBalance)) {
      axis.push(ChartType.AssetBalance);
    }

    if (
      !selectedChartTypes.includes(ChartType.StableBalance) &&
      selectedChartTypes.includes(ChartType.NoAlgoritmicProgression)
    ) {
      axis.push(ChartType.NoAlgoritmicProgression);
    }

    return axis;
  }, [selectedChartTypes]);

  const selectedStableValueChartTypes: ChartType[] = useMemo(() => {
    return selectedChartTypes.filter(
      (ct) =>
        ct === ChartType.NoAlgoritmicProgression ||
        ct === ChartType.StableBalance,
    );
  }, [selectedChartTypes]);

  const selectedAssetValueChartTypes: ChartType[] = useMemo(() => {
    return selectedChartTypes.filter((ct) => ct === ChartType.AssetBalance);
  }, [selectedChartTypes]);

  /** ====================================================   Stable data points ==================================================== */
  const rawStableDataPoints = useMemo(() => {
    return Object.entries(data.endBalanceInUsdtHistory)
      .map(([timestamp, value]) => ({ x: parseInt(timestamp), y: value }))
      .filter(
        (d) =>
          !zoomedDomain ||
          ((zoomedDomain.x[0] as number) -
            ((zoomedDomain.x[1] as number) - (zoomedDomain.x[0] as number)) *
              xDomainBuffer <=
            d.x &&
            d.x <=
              (zoomedDomain.x[1] as number) +
                ((zoomedDomain.x[1] as number) -
                  (zoomedDomain.x[0] as number)) *
                  xDomainBuffer),
      );
  }, [data.endBalanceInUsdtHistory, zoomedDomain]);
  const rawNapDataPoints = useMemo(() => {
    if (!data.priceHistory) {
      return [];
    }

    const datapoints = Object.entries(data.priceHistory).map(
      ([timestamp, balance]) => ({
        x: parseInt(timestamp),
        y: balance,
      }),
    );

    const diff = Math.ceil(datapoints.length / rawStableDataPoints.length);
    const filteredDatapoints = datapoints.filter((d, i) => i % diff === 0);

    // Now we must put these dataPoints on the same y axis as the stable balance
    // First datapoints must be set equal to the stable balance first datapoints
    // if the second datapoints raises 0.1%, do this to the new first datapoint to get the second one
    const updatedDatapoints: { x: number; y: number }[] = [];
    const firstStableBalanceDatapoint = rawStableDataPoints[0];

    for (let i = 0; i < filteredDatapoints.length; i++) {
      if (i === 0) {
        updatedDatapoints.push(firstStableBalanceDatapoint);
        continue;
      }

      const datapoint = filteredDatapoints[i];
      const previousDatapoint = filteredDatapoints[i - 1].y;
      const change = 1 + (datapoint.y - previousDatapoint) / previousDatapoint;
      const latestNewestValue =
        updatedDatapoints[updatedDatapoints.length - 1].y;
      updatedDatapoints.push({ x: datapoint.x, y: latestNewestValue * change });
    }

    return updatedDatapoints;
  }, [data.priceHistory, rawStableDataPoints]);
  const rawAssetDataPoints = useMemo(() => {
    return Object.entries(data.endBalanceInAssetHistory)
      .map(([timestamp, value]) => ({ x: parseInt(timestamp), y: value }))
      .filter(
        (d) =>
          !zoomedDomain ||
          ((zoomedDomain.x[0] as number) -
            ((zoomedDomain.x[1] as number) - (zoomedDomain.x[0] as number)) *
              xDomainBuffer <=
            d.x &&
            d.x <=
              (zoomedDomain.x[1] as number) +
                ((zoomedDomain.x[1] as number) -
                  (zoomedDomain.x[0] as number)) *
                  xDomainBuffer),
      );
  }, [data.endBalanceInAssetHistory, zoomedDomain]);

  const rawNapDataPointsMax = useMemo(
    () => Math.max(...rawNapDataPoints.map((datum) => datum.y)),
    [rawNapDataPoints],
  );
  const rawStableDataPointsMax = useMemo(
    () => Math.max(...rawStableDataPoints.map((datum) => datum.y)),
    [rawStableDataPoints],
  );
  const rawAssetDataPointsMax = useMemo(
    () => Math.max(...rawAssetDataPoints.map((datum) => datum.y)),
    [rawAssetDataPoints],
  );

  const stableValueMax = useMemo(() => {
    return Math.max(
      ...selectedStableValueChartTypes.map((ct) => {
        switch (ct) {
          case ChartType.NoAlgoritmicProgression:
            return rawNapDataPointsMax;

          case ChartType.StableBalance:
            return rawStableDataPointsMax;

          default:
            return 0;
        }
      }),
    );
  }, [
    rawNapDataPointsMax,
    rawStableDataPointsMax,
    selectedStableValueChartTypes,
  ]);
  const assetValueMax = useMemo(() => {
    return Math.max(
      ...selectedAssetValueChartTypes.map((ct) => {
        switch (ct) {
          case ChartType.AssetBalance:
            return rawAssetDataPointsMax;

          default:
            return 0;
        }
      }),
    );
  }, [rawAssetDataPointsMax, selectedAssetValueChartTypes]);

  const napDataOriginalDomain = useMemo(() => {
    return rawNapDataPoints.map((datum) => ({
      ...datum,
      y: datum.y / stableValueMax,
    }));
  }, [rawNapDataPoints, stableValueMax]);
  const stableDataOriginalDomain = useMemo(() => {
    return rawStableDataPoints.map((datum) => ({
      ...datum,
      y: datum.y / stableValueMax,
    }));
  }, [rawStableDataPoints, stableValueMax]);
  const assetDataOriginalDomain = useMemo(() => {
    return rawAssetDataPoints.map((datum) => ({
      ...datum,
      y: datum.y / assetValueMax,
    }));
  }, [assetValueMax, rawAssetDataPoints]);

  const stableValueOriginalDomainMin = useMemo(() => {
    return Math.min(
      ...selectedStableValueChartTypes.reduce<number[]>((acc, ct) => {
        switch (ct) {
          case ChartType.NoAlgoritmicProgression:
            acc.push(...napDataOriginalDomain.map((d) => d.y));
            return acc;

          case ChartType.StableBalance:
            acc.push(...stableDataOriginalDomain.map((d) => d.y));
            return acc;

          default:
            return acc;
        }
      }, []),
    );
  }, [
    napDataOriginalDomain,
    selectedStableValueChartTypes,
    stableDataOriginalDomain,
  ]);
  const stableValueOriginalDomainMax = useMemo(() => {
    return Math.max(
      ...selectedStableValueChartTypes.reduce<number[]>((acc, ct) => {
        switch (ct) {
          case ChartType.NoAlgoritmicProgression:
            acc.push(...napDataOriginalDomain.map((d) => d.y));
            return acc;

          case ChartType.StableBalance:
            acc.push(...stableDataOriginalDomain.map((d) => d.y));
            return acc;

          default:
            return acc;
        }
      }, []),
    );
  }, [
    napDataOriginalDomain,
    selectedStableValueChartTypes,
    stableDataOriginalDomain,
  ]);
  const assetValueOriginalDomainMin = useMemo(() => {
    return Math.min(
      ...selectedAssetValueChartTypes.reduce<number[]>((acc, ct) => {
        switch (ct) {
          case ChartType.AssetBalance:
            acc.push(...assetDataOriginalDomain.map((d) => d.y));
            return acc;

          default:
            return acc;
        }
      }, []),
    );
  }, [assetDataOriginalDomain, selectedAssetValueChartTypes]);

  const assetValueOriginalDomainMax = useMemo(() => {
    return Math.max(
      ...selectedAssetValueChartTypes.reduce<number[]>((acc, ct) => {
        switch (ct) {
          case ChartType.AssetBalance:
            acc.push(...assetDataOriginalDomain.map((d) => d.y));
            return acc;

          default:
            return acc;
        }
      }, []),
    );
  }, [assetDataOriginalDomain, selectedAssetValueChartTypes]);

  const napData = useMemo(() => {
    return napDataOriginalDomain.map((datum) => ({
      ...datum,
      y: stretchYValueOnSharedDomain(
        datum.y,
        stableValueOriginalDomainMin,
        stableValueOriginalDomainMax,
      ),
    }));
  }, [
    napDataOriginalDomain,
    stableValueOriginalDomainMin,
    stableValueOriginalDomainMax,
  ]);
  const stableData = useMemo(() => {
    return stableDataOriginalDomain.map((datum) => ({
      ...datum,
      y: stretchYValueOnSharedDomain(
        datum.y,
        stableValueOriginalDomainMin,
        stableValueOriginalDomainMax,
      ),
    }));
  }, [
    stableDataOriginalDomain,
    stableValueOriginalDomainMin,
    stableValueOriginalDomainMax,
  ]);
  const assetData = useMemo(() => {
    return assetDataOriginalDomain.map((datum) => ({
      ...datum,
      y: stretchYValueOnSharedDomain(
        datum.y,
        assetValueOriginalDomainMin,
        assetValueOriginalDomainMax,
      ),
    }));
  }, [
    assetDataOriginalDomain,
    assetValueOriginalDomainMax,
    assetValueOriginalDomainMin,
  ]);

  const isPositiveChart = useMemo(() => {
    if (rawStableDataPoints.length < 2) {
      return false;
    }

    return (
      rawStableDataPoints[rawStableDataPoints.length - 1].y -
        rawStableDataPoints[0].y >
      0
    );
  }, [rawStableDataPoints]);

  /** ==================================================== Transaction data points ==================================================== */
  const compactTransactionDataPoints = useMemo(() => {
    if (!areTransactionProfitsVisible) {
      return [];
    }

    const dataPoints = allCompactTransactions.map((transaction) => {
      const profit =
        transaction.orderType === 'BUY'
          ? transaction.profitInAsset
          : transaction.profitInUsdt;

      return {
        y: Math.abs(profit),
        x: transaction.timestamp,
        color: profit > 0 ? colors.green : colors.red,
        orderType: transaction.orderType,
      };
    });

    const filteredDataPoints = dataPoints.filter((d) => {
      const isOnlyStableVisible =
        selectedChartTypes[0] === ChartType.StableBalance &&
        selectedChartTypes.length === 1;

      const isOnlyAssetVisible =
        selectedChartTypes[0] === ChartType.AssetBalance &&
        selectedChartTypes.length === 1;

      if (isOnlyStableVisible) {
        return d.orderType !== 'BUY';
      }

      if (isOnlyAssetVisible) {
        return d.orderType !== 'SELL';
      }

      return true;
    });

    return filteredDataPoints.map((d) => ({ ...d, y: Math.min(0.4, d.y / 3) }));
  }, [
    allCompactTransactions,
    areTransactionProfitsVisible,
    selectedChartTypes,
  ]);

  const calculateTransactionDotYValue = useCallback(
    (transaction: CompactTransaction) => {
      let dataSet = [];

      if (selectedChartTypes.includes(ChartType.NoAlgoritmicProgression)) {
        dataSet = napData;
      } else if (selectedChartTypes.includes(ChartType.StableBalance)) {
        dataSet = stableData;
      } else {
        dataSet = assetData;
      }

      return dataSet.reduce(
        (acc, curr) => {
          const diffData = Math.abs(
            acc.timestamp - (transaction.timestamp || 0),
          );
          const diffTransaction = Math.abs(
            curr.x - (transaction.timestamp || 0),
          );

          if (diffTransaction < diffData) {
            acc.timestamp = curr.x;
            acc.value = curr.y;
          }

          return acc;
        },
        { timestamp: 0, value: 0 },
      );
    },
    [assetData, napData, selectedChartTypes, stableData],
  );

  const generateTooltipModel = useCallback(
    (transaction: Transaction): TooltipModel => {
      const type = transaction.transactionType ?? '';
      const isStop = type.includes('STOP_LOSS');
      const isTp = type.includes('TAKE_PROFIT') && !isStop;
      const isBuy = transaction.orderType === 'BUY';
      // Profit is realised in the asset for a BUY, in stable for a SELL.
      const profitValue = isBuy
        ? transaction.profitInAsset
        : transaction.profitInUsdt;
      return {
        title: isStop
          ? 'STOP LOSS'
          : isTp
          ? 'TAKE PROFIT'
          : `CORE ${transaction.orderType}`,
        accent: isStop ? colors.blue : isTp ? colors.yellow : colors.white,
        badge: `#${transaction.id}`,
        rows: [
          {
            label: 'Date',
            value: moment(transaction.timestamp).format('DD MMM YYYY HH:mm'),
          },
          {
            label: 'Price',
            value: formatPrice(transaction.orderPrice, true, 2),
          },
          {
            label: isBuy ? 'Asset profit' : 'Stable profit',
            value: formatPercentage(profitValue),
            tone: profitValue < 0 ? 'neg' : profitValue > 0 ? 'pos' : undefined,
          },
          {
            label: 'Balance',
            value: formatBalance(transaction.totalBalanceValue),
          },
        ],
      };
    },
    [],
  );

  /** Transactions Data points */
  const transactionDataPoints = useMemo(() => {
    const allTransactions = data.transactions.filter((transaction) => {
      if (
        transaction.transactionType.includes('TAKE_PROFIT') &&
        transaction.transactionType.includes('STOP_LOSS') &&
        transactionTypeToggleValues.stopLoss
      ) {
        return true;
      }

      if (
        transaction.transactionType.includes('TAKE_PROFIT') &&
        !transaction.transactionType.includes('STOP_LOSS') &&
        transactionTypeToggleValues.takeProfit
      ) {
        return true;
      }

      if (
        !transaction.transactionType.includes('TAKE_PROFIT') &&
        !transaction.transactionType.includes('STOP_LOSS') &&
        transactionTypeToggleValues.core
      ) {
        return true;
      }
    });

    const dataPoints = allTransactions.map((t) => {
      const isBuy = t.orderType === 'BUY';
      const tooltip = generateTooltipModel(t);

      const { timestamp, value } = calculateTransactionDotYValue(t);

      return {
        x: timestamp,
        y: value,
        isBuy,
        color: isBuy ? colors.green : colors.red,
        tooltip,
        strokeColor: t.transactionType.includes('STOP_LOSS')
          ? colors.blue
          : t.transactionType.includes('TAKE_PROFIT')
          ? colors.yellow
          : colors.white,
        size: t.transactionType.includes('STOP_LOSS')
          ? 3
          : t.transactionType.includes('TAKE_PROFIT')
          ? 3
          : 4,
      };
    });

    return dataPoints;
  }, [
    calculateTransactionDotYValue,
    data.transactions,
    generateTooltipModel,
    transactionTypeToggleValues.core,
    transactionTypeToggleValues.stopLoss,
    transactionTypeToggleValues.takeProfit,
  ]);

  const originalSelectedDataPoints = useMemo(
    () =>
      selectedChartTypes.map((chartType) => {
        switch (chartType) {
          case ChartType.NoAlgoritmicProgression:
            return Object.entries(rawNapDataPoints).map((d) => ({
              x: parseInt(d[0]),
              y: d[1],
            }));

          case ChartType.StableBalance:
            return Object.entries(data.endBalanceInUsdtHistory).map((d) => ({
              x: parseInt(d[0]),
              y: d[1],
            }));

          case ChartType.AssetBalance:
            return Object.entries(data.endBalanceInAssetHistory).map((d) => ({
              x: parseInt(d[0]),
              y: d[1],
            }));
        }
      }),
    [
      data.endBalanceInAssetHistory,
      data.endBalanceInUsdtHistory,
      rawNapDataPoints,
      selectedChartTypes,
    ],
  );

  const selectedDataPoints = useMemo(
    () =>
      selectedChartTypes.map((chartType) => {
        switch (chartType) {
          case ChartType.NoAlgoritmicProgression:
            return napData;

          case ChartType.StableBalance:
            return stableData;

          case ChartType.AssetBalance:
            return assetData;
        }
      }),
    [assetData, napData, selectedChartTypes, stableData],
  );

  const originalDomainX = useMemo(() => {
    let minX = new Date().valueOf();
    let maxX = 0;

    originalSelectedDataPoints.forEach((selectedDataPoint) => {
      selectedDataPoint.forEach((datum) => {
        minX = Math.min(minX, datum.x);
        maxX = Math.max(maxX, datum.x);
      });
    });

    return [minX, maxX] as DomainTuple;
  }, [originalSelectedDataPoints]);

  const { domain } = useAnimatedChartData(
    data.baseAsset.toString(),
    selectedDataPoints,
    selectedChartTypes,
    false,
    1,
    disableAnimation,
  );

  const animatedDataPoints = selectedDataPoints;
  const isAnimatedFinished = true;

  useEffect(() => {
    setIsChartAnimationDone(isAnimatedFinished);
  }, [isAnimatedFinished, setIsChartAnimationDone]);

  // const domain = useMemo(() => {
  //   if (zoomedDomain) {
  //     return zoomedDomain;
  //   }

  //   let minY = 1;
  //   let maxY = 0;
  //   let minX = new Date().valueOf();
  //   let maxX = 0;

  //   selectedDataPoints.forEach((selectedDataPoint) => {
  //     selectedDataPoint.forEach((datum) => {
  //       minX = Math.min(minX, datum.x);
  //       maxX = Math.max(maxX, datum.x);
  //       minY = Math.min(minY, datum.y);
  //       maxY = Math.max(maxY, datum.y);
  //     });
  //   });

  //   return {
  //     x: [minX, maxX] as DomainTuple,
  //     y: [minY * 0.1, maxY * 1.03] as DomainTuple,
  //   };
  // }, [selectedDataPoints, zoomedDomain]);

  const axisStyle = useMemo(
    () => ({
      axis: { stroke: 'rgba(64,64,64,0.7)' },
      grid: {
        stroke: hideGrid ? 'transparent' : 'rgba(64,64,64,0.7)',
      },
      ticks: { stroke: 'white', size: 2 },
      tickLabels: { fontSize: 14, padding: 5, fill: colors.white },
    }),
    [hideGrid],
  );

  const botColor = useMemo(() => {
    const color = getBotColor(data.baseAsset);

    return calculateIsColorTooDark(color) ? lightenColor(color) : color;
  }, [data.baseAsset]);

  const tickValues = useMemo(() => {
    if (!domain.x[0]) {
      return [];
    }

    return createXTickValues(domain.x[0] as number);
  }, [domain.x]);

  /** Determine on end - start date */
  const calculateTickValue = useCallback(
    (t: Date) => {
      if (!domain?.x) {
        return moment(t).format('DD MMM');
      }

      const formatUnit = getFormatUnit(
        domain.x[0] as number,
        domain.x[1] as number,
      );

      return moment(t).format(formatUnit);
    },
    [domain?.x],
  );

  const calculateYTickValue = useCallback(
    (axisIndex: number, t: any) => {
      const chartType = selectedChartTypesAxis[axisIndex]; // The first axis
      const isStableValue = chartType !== ChartType.AssetBalance;

      let max = 0;
      let minO = 0;
      let maxO = 0;

      if (
        (ChartType.NoAlgoritmicProgression === chartType ||
          ChartType.StableBalance === chartType) &&
        selectedStableValueChartTypes.includes(chartType)
      ) {
        max = stableValueMax;
        minO = stableValueOriginalDomainMin;
        maxO = stableValueOriginalDomainMax;
      }

      if (
        ChartType.AssetBalance === chartType &&
        selectedAssetValueChartTypes.includes(chartType)
      ) {
        max = assetValueMax;
        minO = assetValueOriginalDomainMin;
        maxO = assetValueOriginalDomainMax;
      }

      /** Reverse the stretch to get the original value */
      const y = maxO - (1 - t) * ((maxO - minO) * 1.05);
      const balance = y * max;

      return `${formatPrice(
        balance,
        isStableValue,
        isStableValue ? 0 : balance < 10 ? data.maxDecimals ?? 6 : 0,
      )}`;
    },
    [
      assetValueMax,
      assetValueOriginalDomainMax,
      assetValueOriginalDomainMin,
      data.maxDecimals,
      selectedAssetValueChartTypes,
      selectedChartTypesAxis,
      selectedStableValueChartTypes,
      stableValueMax,
      stableValueOriginalDomainMax,
      stableValueOriginalDomainMin,
    ],
  );

  // Nice round balance values for a y-axis, mapped back to their stretched
  // y-positions — the axis then shows ~8-10 clean full numbers (like the
  // candlestick charts) instead of an awkward 3.
  const calculateYTickPositions = useCallback(
    (axisIndex: number): number[] => {
      const chartType = selectedChartTypesAxis[axisIndex];

      let max = 0;
      let minO = 0;
      let maxO = 0;

      if (
        (ChartType.NoAlgoritmicProgression === chartType ||
          ChartType.StableBalance === chartType) &&
        selectedStableValueChartTypes.includes(chartType)
      ) {
        max = stableValueMax;
        minO = stableValueOriginalDomainMin;
        maxO = stableValueOriginalDomainMax;
      }

      if (
        ChartType.AssetBalance === chartType &&
        selectedAssetValueChartTypes.includes(chartType)
      ) {
        max = assetValueMax;
        minO = assetValueOriginalDomainMin;
        maxO = assetValueOriginalDomainMax;
      }

      if (!max || maxO <= minO) {
        return [];
      }

      const span = (maxO - minO) * 1.05;
      return niceLinearTicks(minO * max, maxO * max, 9)
        .values.map((balance) => 1 - (maxO - balance / max) / span)
        .filter((y) => y >= -0.02 && y <= 1.02);
    },
    [
      assetValueMax,
      assetValueOriginalDomainMax,
      assetValueOriginalDomainMin,
      selectedAssetValueChartTypes,
      selectedChartTypesAxis,
      selectedStableValueChartTypes,
      stableValueMax,
      stableValueOriginalDomainMax,
      stableValueOriginalDomainMin,
    ],
  );

  const getChartColor = useCallback(
    (chartType: ChartType) => {
      switch (chartType) {
        case ChartType.NoAlgoritmicProgression:
          return {
            fill: 'url(#priceColor)',
            stroke: colors.white,
          };

        case ChartType.AssetBalance:
          return {
            fill: 'url(#botColor)',
            stroke: botColor,
          };

        case ChartType.StableBalance:
          return {
            fill: `url(#${
              isPositiveChart ? 'positiveStableColor' : 'negativeStableColor'
            })`,
            stroke: isPositiveChart ? colors.mossGreen : colors.red,
          };
      }
    },
    [botColor, isPositiveChart],
  );

  const calculateYAxisWidthByChartType = useCallback(
    (index: number) => {
      const yTicks = calculateYTickPositions(index);
      const longestTickWidth = calculateLongestTickWidth(yTicks, (t: number) =>
        calculateYTickValue(index, t),
      );
      const yAxisWidth = calculateYAxisWidth(longestTickWidth);
      return { width: yAxisWidth, longestTickWidth };
    },
    [calculateYTickValue, calculateYTickPositions],
  );

  // Create information per selected chart type
  const yAxisInformation = useMemo(() => {
    return selectedChartTypesAxis.map((_, index) =>
      calculateYAxisWidthByChartType(index),
    );
  }, [calculateYAxisWidthByChartType, selectedChartTypesAxis]);

  const getYAxisStyle = useCallback(
    (chartType: ChartType, index: number) => {
      const width = yAxisInformation[index]?.width || 0;
      const longestTickWidth = yAxisInformation[index]?.longestTickWidth ?? 0;

      return {
        axis: { stroke: 'rgba(64,64,64,0.7)' },
        grid: {
          stroke: hideGrid ? 'transparent' : 'rgba(64,64,64,0.7)',
        },
        ticks: { stroke: 'white', size: 2 },
        tickLabels: {
          fontSize: 16,
          padding: (width - longestTickWidth) / 2,
          fill: getChartColor(chartType).stroke,
        },
      };
    },
    [getChartColor, yAxisInformation, hideGrid],
  );

  const chartPadding = useMemo(
    () => ({
      left: yAxisInformation[1]?.width ?? 30,
      top: 5,
      right: yAxisInformation[0].width ?? 60,
      bottom: hideXAxis ? 0 : 60,
    }),
    [yAxisInformation, hideXAxis],
  );

  useEffect(() => {
    // Surface the left y-axis width so an external legend can offset itself.
    onLeftAxisWidthChange?.(
      yAxisInformation.length >= 2 ? yAxisInformation[1]?.width ?? 0 : 0,
    );
  }, [onLeftAxisWidthChange, yAxisInformation]);

  const timeAxisDomainStart = domain?.x ? (domain.x[0] as number) : undefined;
  const timeAxisDomainEnd = domain?.x ? (domain.x[1] as number) : undefined;
  useEffect(() => {
    // Surface the plot rectangle + time domain so an external x-axis footer
    // can position its cells exactly under the chart's vertical gridlines.
    if (
      timeAxisDomainStart === undefined ||
      timeAxisDomainEnd === undefined ||
      timeAxisDomainEnd <= timeAxisDomainStart
    ) {
      return;
    }
    onTimeAxisChange?.({
      paddingLeft: chartPadding.left,
      paddingRight: chartPadding.right,
      domainStart: timeAxisDomainStart,
      domainEnd: timeAxisDomainEnd,
    });
  }, [
    onTimeAxisChange,
    chartPadding.left,
    chartPadding.right,
    timeAxisDomainStart,
    timeAxisDomainEnd,
  ]);

  const handleTransactionDotPress = useCallback(
    (
      event: React.SyntheticEvent<any, Event>,
      eventProps: { index: number; data: Datum[]; datum: Datum },
    ) => {
      const { isBuy } = eventProps.datum;
      const isStableValueVisible = selectedChartTypes.includes(
        ChartType.StableBalance,
      );

      const startIndex =
        eventProps.index -
        (isBuy ? (isStableValueVisible ? 0 : 1) : isStableValueVisible ? 1 : 0);
      const endIndex =
        eventProps.index +
        (isBuy ? (isStableValueVisible ? 2 : 1) : isStableValueVisible ? 1 : 2);

      const zoomedTransactionDots = eventProps.data.slice(
        startIndex === -1 ? 0 : startIndex,
        endIndex,
      );

      let minX = domain.x[0] as number;
      let maxX = zoomedTransactionDots[zoomedTransactionDots.length - 1].x;

      if (startIndex !== -1) {
        minX = zoomedTransactionDots[0].x;
      }

      if (minX === maxX) {
        maxX = napData[napData.length - 1].x || new Date().valueOf();
      }

      const diffX = maxX - minX;
      const bufferSize = diffX * xDomainBuffer;

      const minDomainX = Math.max(
        minX - bufferSize,
        originalDomainX[0] as number,
      );
      const maxDomainX = Math.min(
        maxX + bufferSize,
        originalDomainX[1] as number,
      );

      const zoomedDomain = {
        x: [minDomainX, maxDomainX] as DomainTuple,
        y: [0, 1] as DomainTuple,
      };

      setZoomedDomain(zoomedDomain);
      setZoomedStartIndex(Math.max(-1, startIndex));
      setZoomLevel(1);

      return null;
    },
    [domain.x, originalDomainX, napData, selectedChartTypes],
  );

  const handleZoomOut = useCallback(() => {
    setZoomedDomain(undefined);
    setZoomLevel(1);
  }, []);

  const handleZoom = useCallback(
    (startIndex: number, passedZoomLevel?: number) => {
      const isPriceVisible = selectedChartTypes.includes(
        ChartType.NoAlgoritmicProgression,
      );
      const zoomedTransactionDots = transactionDataPoints.slice(
        startIndex === -1 ? 0 : startIndex,
        startIndex + (passedZoomLevel || zoomLevel) * 2,
      );

      let minX = zoomedTransactionDots[0].x;
      let maxX = zoomedTransactionDots[zoomedTransactionDots.length - 1].x;

      if (zoomedTransactionDots.length === 1) {
        if (startIndex === -1) {
          minX = originalDomainX[0] as number;
        } else {
          maxX = originalDomainX[1] as number;
        }
      }

      if (isPriceVisible && startIndex === -1) {
        minX = originalDomainX[0] as number;
      }

      if (
        isPriceVisible &&
        startIndex + (passedZoomLevel || zoomLevel) >=
          transactionDataPoints.length
      ) {
        maxX = originalDomainX[1] as number;
      }

      if (!minX || !maxX) {
        return;
      }

      const diffX = maxX - minX;
      const bufferSize =
        (diffX * xDomainBuffer) / (passedZoomLevel || zoomLevel);

      const minDomainX = Math.max(
        minX - bufferSize,
        originalDomainX[0] as number,
      );
      const maxDomainX = Math.min(
        maxX + bufferSize,
        originalDomainX[1] as number,
      );

      const zoomedDomain = {
        x: [minDomainX, maxDomainX] as DomainTuple,
        y: [0, 1] as DomainTuple,
      };

      setZoomedDomain(zoomedDomain);
      setZoomedStartIndex(Math.max(-1, startIndex));
    },
    [originalDomainX, selectedChartTypes, transactionDataPoints, zoomLevel],
  );

  const handleZoomLeft = useCallback(() => {
    if (typeof zoomedStartIndex === 'undefined' || zoomedStartIndex === -1) {
      return;
    }

    const isPriceOnly =
      selectedChartTypes.length === 1 &&
      selectedChartTypes.includes(ChartType.NoAlgoritmicProgression);

    handleZoom(zoomedStartIndex - (isPriceOnly ? 1 : 2));
  }, [handleZoom, selectedChartTypes, zoomedStartIndex]);

  const handleZoomRight = useCallback(() => {
    if (typeof zoomedStartIndex === 'undefined') {
      return;
    }

    const isPriceOnly =
      selectedChartTypes.length === 1 &&
      selectedChartTypes.includes(ChartType.NoAlgoritmicProgression);
    const range = isPriceOnly ? 1 : 2;

    const maxTransactionDots = transactionDataPoints.length;
    if (zoomedStartIndex + range * zoomLevel >= maxTransactionDots) {
      return;
    }

    handleZoom(zoomedStartIndex + range);
  }, [
    zoomedStartIndex,
    selectedChartTypes,
    transactionDataPoints.length,
    zoomLevel,
    handleZoom,
  ]);

  const handleLowerZoomLevel = useCallback(() => {
    if (typeof zoomedStartIndex === 'undefined') {
      return;
    }

    const updatedZoomLevel = Math.max(1, zoomLevel - 1);
    setZoomLevel(updatedZoomLevel);

    handleZoom(zoomedStartIndex, updatedZoomLevel);
  }, [handleZoom, zoomLevel, zoomedStartIndex]);

  const handleRaiseZoomLevel = useCallback(() => {
    if (typeof zoomedStartIndex === 'undefined') {
      return;
    }

    const isPriceVisible = selectedChartTypes.includes(
      ChartType.NoAlgoritmicProgression,
    );
    const range = isPriceVisible ? 1 : 2;

    const updatedZoomLevel = Math.min(
      isPriceVisible
        ? transactionDataPoints.length + 1
        : transactionDataPoints.length / 2,
      zoomLevel + 1,
    );
    setZoomLevel(updatedZoomLevel);

    let startIndex = zoomedStartIndex;

    if (isPriceVisible) {
      if (zoomedStartIndex + updatedZoomLevel >= transactionDataPoints.length) {
        startIndex--;
      }
    } else {
      if (
        zoomedStartIndex + range * updatedZoomLevel >=
        transactionDataPoints.length
      ) {
        startIndex = startIndex - 2;
      }
    }

    handleZoom(startIndex, updatedZoomLevel);
  }, [
    handleZoom,
    selectedChartTypes,
    transactionDataPoints.length,
    zoomLevel,
    zoomedStartIndex,
  ]);

  const toggleTransactionProfits = useCallback(() => {
    setAreTransactionProfitsVisible((prev) => !prev);
  }, [setAreTransactionProfitsVisible]);

  const toggleTransactions = useCallback(() => {
    setIsTransactionsSelected((prev) => !prev);
  }, [setIsTransactionsSelected]);

  const monthlyProfitDataSets = useMemo(() => {
    /**
     * We start at the beginning of the stableData
     * For the first one, we go until the end of the month
     * For the rest we beginning of month until the end
     * for the last one, beginning of month until now
     */
    const isStableChartSelected = selectedChartTypes.includes(
      ChartType.StableBalance,
    );
    const isPriceChartSelected = selectedChartTypes.includes(
      ChartType.NoAlgoritmicProgression,
    );

    const data =
      isStableChartSelected || isPriceChartSelected ? stableData : assetData;

    if (!data?.length) {
      return [];
    }

    let endTimestamp = Math.min(
      new Date(
        new Date(data[0].x).getFullYear(),
        new Date(data[0].x).getMonth() + 1,
        0,
      ).valueOf(),
      new Date().valueOf(),
    );

    let startBalance = data[0].y;
    let previousBalance = data[0].y;
    const dataSets: Array<{
      data: Array<{
        x: number;
        y: number;
        y0?: number;
        color: string;
        date?: Date;
      }>;
      color?: string;
    }> = [];

    for (let i = 0; i < data.length; i++) {
      const datum = data[i];
      const profit = (datum.y - startBalance) / startBalance;

      if (datum.x <= endTimestamp) {
        if (!dataSets.length) {
          dataSets.push({ data: [] });
        }

        dataSets[dataSets.length - 1].data.push({
          x: datum.x,
          date: new Date(datum.x),
          y: Math.abs(profit),
          color:
            profit >= 0
              ? 'url(#positiveMonthGradient)'
              : 'url(#negativeMonthGradient)',
        });
        previousBalance = datum.y;
      } else {
        if (!dataSets.length) {
          dataSets.push({ data: [] });
        }

        endTimestamp = new Date(
          new Date(datum.x).getFullYear(),
          new Date(datum.x).getMonth() + 1,
          0,
        ).valueOf();
        startBalance = previousBalance;

        if (dataSets[dataSets.length - 1].data.length) {
          dataSets.push({
            data: [
              {
                x: new Date(
                  new Date(datum.x).getFullYear(),
                  new Date(datum.x).getMonth(),
                  0,
                ).valueOf(),
                date: new Date(
                  new Date(datum.x).getFullYear(),
                  new Date(datum.x).getMonth(),
                  0,
                ),
                y: Math.abs(profit),
                color:
                  profit >= 0
                    ? 'url(#positiveMonthGradient)'
                    : 'url(#negativeMonthGradient)',
              },
            ],
          });
        }
      }
    }

    if (!domain) {
      return dataSets;
    }

    /** Update the y value to stretch the first 30% of the y-axis */

    const max = Math.max(
      ...dataSets.map(({ data }) => Math.max(...data.map((datum) => datum.y))),
    );

    dataSets.forEach((dataSet) => {
      const { data } = dataSet;

      data.forEach((datum) => {
        const currentY =
          (domain.y[0] as number) +
          (datum.y / max) *
            ((domain.y[1] as number) - (domain.y[0] as number)) *
            0.1;

        datum.y = currentY;
      });

      if (data.length) {
        const lastDatum = data[data.length - 1];
        dataSet.color = lastDatum.color;
      }
    });

    return dataSets;
  }, [assetData, domain, selectedChartTypes, stableData]);

  // Vertical gridline at each adaptive time-axis boundary inside the visible
  // domain, positioned by real timestamp so it lines up with the data. The
  // interval granularity (year → quarter → month → week) follows the span.
  const timeGridlines = useMemo(() => {
    if (!showTimeGridlines || !domain?.x || !domain?.y) {
      return [];
    }
    const yBottom = domain.y[0] as number;
    const yTop = domain.y[1] as number;
    const { intervals } = buildTimeAxis(
      domain.x[0] as number,
      domain.x[1] as number,
    );
    // One line at each interior boundary — the first interval starts at the
    // domain edge, so it needs no line.
    return intervals.slice(1).map((interval) => [
      { x: interval.startTs, y: yBottom },
      { x: interval.startTs, y: yTop },
    ]);
  }, [showTimeGridlines, domain]);

  /** ============================ Hover crosshair ============================ */
  const [cursorPixelX, setCursorPixelX] = useState<number | undefined>(
    undefined,
  );

  const handleCrosshairMove = useCallback((e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setCursorPixelX(e.clientX - rect.left);
  }, []);

  const handleCrosshairLeave = useCallback(() => {
    setCursorPixelX(undefined);
  }, []);

  // Cursor pixel → timestamp, via the chart's inner plot rectangle.
  const cursorTimestamp = useMemo(() => {
    if (cursorPixelX === undefined || !domain?.x) {
      return null;
    }
    const domainStart = domain.x[0] as number;
    const domainEnd = domain.x[1] as number;
    const plotWidth =
      rootDimensions.width - chartPadding.left - chartPadding.right;
    if (plotWidth <= 0) {
      return null;
    }
    const ratio = (cursorPixelX - chartPadding.left) / plotWidth;
    if (ratio < 0 || ratio > 1) {
      return null;
    }
    return domainStart + ratio * (domainEnd - domainStart);
  }, [cursorPixelX, domain, chartPadding, rootDimensions.width]);

  // The nearest point of every visible series — drives the dots + tooltip.
  const cursorSeries = useMemo(() => {
    if (cursorTimestamp == null) {
      return [];
    }
    return selectedChartTypes
      .map((chartType, index) => {
        const normalised = animatedDataPoints[index];
        if (!normalised?.length) {
          return null;
        }
        const rawPoints =
          chartType === ChartType.NoAlgoritmicProgression
            ? rawNapDataPoints
            : chartType === ChartType.StableBalance
            ? rawStableDataPoints
            : rawAssetDataPoints;

        let bestIndex = 0;
        for (let i = 1; i < normalised.length; i++) {
          if (
            Math.abs(normalised[i].x - cursorTimestamp) <
            Math.abs(normalised[bestIndex].x - cursorTimestamp)
          ) {
            bestIndex = i;
          }
        }
        const point = normalised[bestIndex];
        const rawValue = rawPoints[bestIndex]?.y ?? 0;
        const isAsset = chartType === ChartType.AssetBalance;
        return {
          x: point.x,
          y: point.y,
          fill: getChartColor(chartType).stroke,
          label:
            chartType === ChartType.NoAlgoritmicProgression
              ? 'Price'
              : chartType === ChartType.StableBalance
              ? 'Stable balance'
              : 'Asset balance',
          value: isAsset
            ? formatPrice(rawValue, false, rawValue < 10 ? 6 : 0)
            : formatBalance(rawValue),
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> => !!entry);
  }, [
    cursorTimestamp,
    selectedChartTypes,
    animatedDataPoints,
    rawNapDataPoints,
    rawStableDataPoints,
    rawAssetDataPoints,
    getChartColor,
  ]);

  const cursorDotData = useMemo(
    () => cursorSeries.map(({ x, y, fill }) => ({ x, y, fill })),
    [cursorSeries],
  );

  const cursorLineData = useMemo(() => {
    if (cursorTimestamp == null || !domain?.y) {
      return [];
    }
    return [
      { x: cursorTimestamp, y: domain.y[0] as number },
      { x: cursorTimestamp, y: domain.y[1] as number },
    ];
  }, [cursorTimestamp, domain]);

  const showCrosshair =
    cursorTimestamp != null &&
    cursorPixelX !== undefined &&
    cursorSeries.length > 0;

  const crosshairFlip =
    cursorPixelX !== undefined && cursorPixelX > rootDimensions.width * 0.62;

  return (
    <div
      style={{
        position: 'relative',
        flex: 1,
      }}
      onMouseMove={handleCrosshairMove}
      onMouseLeave={handleCrosshairLeave}
    >
      <svg style={{ position: 'absolute' }}>
        <defs>
          <linearGradient id="priceColor" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={colors.white} stopOpacity={0.3} />
            <stop offset="100%" stopColor={colors.white} stopOpacity={0.04} />
          </linearGradient>
          <linearGradient
            id="positiveStableColor"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colors.mossGreen} stopOpacity={0.3} />
            <stop
              offset="100%"
              stopColor={colors.mossGreen}
              stopOpacity={0.04}
            />
          </linearGradient>
          <linearGradient
            id="negativeStableColor"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colors.red} stopOpacity={0.4} />
            <stop offset="100%" stopColor={colors.red} stopOpacity={0.08} />
          </linearGradient>
          <linearGradient id="botColor" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={botColor} stopOpacity={0.4} />
            <stop offset="100%" stopColor={botColor} stopOpacity={0.08} />
          </linearGradient>
          <linearGradient
            id="positiveMonthGradient"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colors.green} stopOpacity={0.3} />
            <stop offset="100%" stopColor={colors.green} stopOpacity={0.08} />
          </linearGradient>
          <linearGradient
            id="negativeMonthGradient"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colors.red} stopOpacity={0.3} />
            <stop offset="100%" stopColor={colors.red} stopOpacity={0.08} />
          </linearGradient>
        </defs>
      </svg>

      <VictoryChart
        height={rootDimensions.height}
        width={rootDimensions.width}
        scale="linear"
        padding={chartPadding}
        domainPadding={{ y: 5 }}
        domain={domain}
      >
        {selectedChartTypesAxis.map((chartType, index) => (
          <VictoryAxis
            key={`axis_${selectedChartTypes[index]}`}
            dependentAxis
            style={getYAxisStyle(chartType, index)}
            tickValues={calculateYTickPositions(index)}
            orientation={index === 0 ? 'right' : 'left'}
            tickFormat={(t: number) => calculateYTickValue(index, t)}
            width={calculateYAxisWidthByChartType(index).width}
          />
        ))}

        {!hideXAxis && (
          <VictoryAxis
            tickFormat={calculateTickValue}
            style={axisStyle}
            tickValues={tickValues}
            tickCount={8}
            fixLabelOverlap
          />
        )}

        {timeGridlines.map((lineData, index) => (
          <VictoryLine
            key={`time_gridline_${index}`}
            data={lineData}
            style={{
              data: {
                stroke: 'rgba(255,255,255,0.15)',
                strokeWidth: 1,
                pointerEvents: 'none',
              },
            }}
          />
        ))}

        {animatedDataPoints.map((selectedDataPoint, index) => (
          <VictoryArea
            key={`chart_${selectedChartTypes[index]}`}
            data={selectedDataPoint}
            style={{
              data: {
                ...getChartColor(selectedChartTypes[index]),
                strokeWidth: 1,
              },
            }}
            animate={animate}
          />
        ))}

        <VictoryBar
          cornerRadius={2}
          barWidth={5}
          style={{
            data: {
              fill: ({ datum }) => datum.color,
              stroke: colors.white,
              strokeWidth: 0.8,
              opacity: isAnimatedFinished ? 0.6 : 0,
              transition: 'opacity 0.5s',
            },
          }}
          data={compactTransactionDataPoints}
        />

        {/* Crosshair sits behind the transaction dots so the dots stay
         * hoverable; pointer events are off so it never steals a hover. */}
        {showCrosshair && (
          <VictoryLine
            data={cursorLineData}
            style={{
              data: {
                stroke: 'rgba(255,255,255,0.55)',
                strokeWidth: 1,
                pointerEvents: 'none',
              },
            }}
          />
        )}
        {showCrosshair && (
          <VictoryScatter
            data={cursorDotData}
            size={4}
            style={{
              data: {
                fill: ({ datum }) => datum.fill,
                stroke: '#0c0e12',
                strokeWidth: 1.5,
                pointerEvents: 'none',
              },
            }}
          />
        )}

        {isTransactionsSelected && (
          <VictoryScatter
            data={transactionDataPoints}
            labels={({ datum }) => (datum?.tooltip ? ' ' : '')}
            labelComponent={
              <VictoryTooltip
                constrainToVisibleArea
                pointerLength={7}
                flyoutWidth={TOOLTIP_WIDTH}
                flyoutHeight={(p: any) => tooltipModelHeight(p?.datum?.tooltip)}
                flyoutComponent={<ChartTooltip />}
                labelComponent={<g />}
              />
            }
            style={{
              data: {
                fill: ({ datum }) => datum.color,
                stroke: ({ datum }) => datum.strokeColor,
                strokeWidth: ({ datum }) => datum.size / 3,
                opacity: isAnimatedFinished ? 1 : 1,
                transition: 'opacity 0.5s',
              },
            }}
            events={[
              {
                target: 'data',
                eventHandlers: {
                  onClick: handleTransactionDotPress,
                },
              },
            ]}
            size={(d) => (zoomedDomain ? 3 : d.size)}
            dataComponent={
              <Point className="single-profit-calcs-chart-scatter-dot" />
            }
            animate={animate}
          />
        )}

        {!!monthlyProfitDataSets?.length &&
          monthlyProfitDataSets.map(
            ({ data: monthlyProfitDataPoints, color }, index) => (
              <VictoryArea
                key={`monthly_profit_${index}`}
                data={monthlyProfitDataPoints}
                style={{
                  data: {
                    fill: color,
                    opacity: isAnimatedFinished ? 1 : 0,
                    transition: 'opacity 0.5s',
                  },
                }}
              />
            ),
          )}
      </VictoryChart>
      {showCrosshair && cursorPixelX !== undefined && (
        <CrosshairTooltip
          title={moment(cursorTimestamp ?? undefined).format(
            'DD MMM YYYY HH:mm',
          )}
          accent="#eef0f2"
          rows={cursorSeries.map((entry) => ({
            label: entry.label,
            value: entry.value,
            color: entry.fill,
          }))}
          left={cursorPixelX}
          top={8}
          flip={crosshairFlip}
        />
      )}
      {!hideControls && (
        <>
          <ChartTypeSelector
            selectedChartTypes={selectedChartTypes}
            setSelectedChartTypes={setSelectedChartTypes}
            getChartColor={getChartColor}
            areTransactionProfitsVisible={areTransactionProfitsVisible}
            toggleTransactionProfits={toggleTransactionProfits}
            isTransactionsSelected={isTransactionsSelected}
            toggleTransactions={toggleTransactions}
          />
          <TransactionTypeToggles
            useTakeProfitTransactions={useTakeProfitTransactions}
            transactionTypeToggleValues={transactionTypeToggleValues}
            setTransactionTypeToggleValues={setTransactionTypeToggleValues}
          />
        </>
      )}
      {/* <ChartZoomOptions
        zoomedDomain={zoomedDomain}
        selectedChartTypesLength={selectedChartTypes.length}
        handleZoomLeft={handleZoomLeft}
        handleZoomOut={handleZoomOut}
        handleZoomRight={handleZoomRight}
      />
      <ChartZoomLevel
        isVisible={!!zoomedDomain}
        zoomLevel={zoomLevel}
        handleZoomIn={handleLowerZoomLevel}
        handleZoomOut={handleRaiseZoomLevel}
      /> */}
    </div>
  );
};

export default SingleProfitCalculationResultChart;
